import React, { useState, useEffect } from "react";
import { db, storage } from "../../../firebase/config";
import { doc, updateDoc, onSnapshot, getDoc, addDoc, collection, where, orderBy, query } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { useAuth } from "../../../context/AuthContext";
import { useTheme } from "../../../context/ThemeContext";
import Header from "../../../components/Header";
import { registrarAccion } from "../../../utils/trazabilidad";

const DATOS_BANCARIOS_ARS = {
  cuenta: 'CA$ 218-218037244-001',
  alias: 'PAJARO.POETA.REZO',
  cbu: '0340218608218037244018',
  cuil: '20397047564',
  titular: 'Federico Martos',
};

const DATOS_BANCARIOS_USD = {
  cuenta: 'CAU$S 218-218037244-000',
  alias: 'TIRA.PARRA.TORO',
  cbu: '0340218609218037244002',
  cuil: '20397047564',
  titular: 'Federico Martos',
};

const PLANES = {
  starter: { nombre: 'Starter', precioUSD: 29 },
  advanced: { nombre: 'Advanced', precioUSD: 59 },
  pro: { nombre: 'Pro', precioUSD: 99 },
};

export default function Configuracion() {
  const { currentUser, userRole, clinicaId, clinicaData } = useAuth();
  const { theme } = useTheme();
  const [config, setConfig] = useState(null);
  const [facturacion, setFacturacion] = useState(null);
  const [tipoCambio, setTipoCambio] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [guardado, setGuardado] = useState(false);
  const [enviandoPago, setEnviandoPago] = useState(false);
  const [comprobante, setComprobante] = useState(null);
  const [referencia, setReferencia] = useState('');
  const [monedaPago, setMonedaPago] = useState('USD');
  const [form, setForm] = useState({
    nombre: '', medicos: { medico1: '', medico2: '', medico3: '', medico4: '' },
    telefono: '', direccion: '', ciudad: '', provincia: '', web: '', descripcion: '',
  });
  const [historialPagos, setHistorialPagos] = useState([]);

  const btnPrimaryStyle = { background: `linear-gradient(135deg, ${theme.accent}, ${theme.accentDark})`, border: 'none', borderRadius: '8px', padding: '8px 16px', fontSize: '13px', cursor: 'pointer', color: '#FFFFFF', fontWeight: '600', fontFamily: "'Montserrat', sans-serif" };
  const btnSecondaryStyle = { background: 'transparent', border: `0.5px solid ${theme.borderInput}`, borderRadius: '8px', padding: '8px 16px', fontSize: '13px', cursor: 'pointer', color: theme.secondary, fontFamily: "'Montserrat', sans-serif" };
  const inputStyle = { width: '100%', padding: '10px 14px', fontSize: '13px', border: `0.5px solid ${theme.borderInput}`, borderRadius: '8px', outline: 'none', color: theme.primary, background: theme.bgInput, boxSizing: 'border-box', fontFamily: "'Montserrat', sans-serif" };
  const labelStyle = { fontSize: '11px', color: theme.secondary, display: 'block', marginBottom: '6px', fontWeight: '500', letterSpacing: '1px' };

  useEffect(() => {
    if (!clinicaId) return;
    const unsub = onSnapshot(doc(db, 'clinicas', clinicaId), snap => {
      if (snap.exists()) {
        const data = snap.data();
        setConfig(data);
        setForm({
          nombre: data.nombre || '',
          medicos: { medico1: data.medicos?.medico1 || '', medico2: data.medicos?.medico2 || '', medico3: data.medicos?.medico3 || '', medico4: data.medicos?.medico4 || '' },
          telefono: data.telefono || '', direccion: data.direccion || '',
          ciudad: data.ciudad || '', provincia: data.provincia || '',
          web: data.web || '', descripcion: data.descripcion || '',
        });
      }
    });
    return unsub;
  }, [clinicaId]);

  useEffect(() => {
    if (!clinicaId) return;
    const unsub = onSnapshot(collection(db, 'facturacion'), snap => {
      const fact = snap.docs.find(d => d.data().clinicaId === clinicaId);
      if (fact) setFacturacion({ id: fact.id, ...fact.data() });
    });
    return unsub;
  }, [clinicaId]);
  
  useEffect(() => {
    if (!clinicaId) return;
    const unsub = onSnapshot(
      query(collection(db, 'pagos'), where('clinicaId', '==', clinicaId), orderBy('fecha', 'desc')),
      snap => setHistorialPagos(snap.docs.map(d => ({ id: d.id, ...d.data() })))
    );
    return unsub;
  }, [clinicaId]);
  
  useEffect(() => {
    const cargar = async () => {
      const snap = await getDoc(doc(db, 'configuracion_sistema', 'tipo_cambio'));
      if (snap.exists()) setTipoCambio(snap.data());
    };
    cargar();
  }, []);

  async function guardar() {
    if (!clinicaId) return;
    setGuardando(true);
    await updateDoc(doc(db, 'clinicas', clinicaId), {
      nombre: form.nombre, medicos: form.medicos, telefono: form.telefono,
      direccion: form.direccion, ciudad: form.ciudad, provincia: form.provincia,
      web: form.web, descripcion: form.descripcion, configuradoEn: new Date(),
    });
    await registrarAccion(currentUser?.email, userRole, 'Configuración', 'Actualizar configuración', form.nombre, clinicaId);
    setGuardando(false);
    setGuardado(true);
    setTimeout(() => setGuardado(false), 3000);
  }

  async function enviarComprobante() {
    if (!comprobante && !referencia) { alert('Adjuntá un comprobante o ingresá una referencia'); return; }
    setEnviandoPago(true);
    try {
      let comprobanteUrl = null;
      if (comprobante) {
        const storageRef = ref(storage, `comprobantes/${clinicaId}/${Date.now()}_${comprobante.name}`);
        await uploadBytes(storageRef, comprobante);
        comprobanteUrl = await getDownloadURL(storageRef);
      }
      const plan = facturacion?.plan || config?.plan || 'starter';
      const precioUSD = PLANES[plan]?.precioUSD || 0;
      const precioARS = tipoCambio ? precioUSD * tipoCambio.valor : 0;
      const monto = monedaPago === 'USD' ? precioUSD : precioARS;
      await addDoc(collection(db, 'pagos_pendientes'), {
        clinicaId, clinicaNombre: config?.nombre || '',
        monto, moneda: monedaPago, plan,
        referencia, comprobante: comprobanteUrl,
        estado: 'pendiente', fecha: new Date(), enviadoPor: currentUser?.email,
      });
      if (facturacion?.id) {
        await updateDoc(doc(db, 'facturacion', facturacion.id), { pagoPendienteAprobacion: true });
      }
      await registrarAccion(currentUser?.email, userRole, 'Configuración', 'Enviar comprobante de pago', config?.nombre, clinicaId);
      alert('Comprobante enviado. Te avisaremos cuando sea aprobado.');
      setComprobante(null);
      setReferencia('');
    } catch (e) { alert('Error al enviar: ' + e.message); }
    setEnviandoPago(false);
  }

  const plan = facturacion?.plan || config?.plan;
  const precioBaseUSD = plan ? (PLANES[plan]?.precioUSD || 0) : 0;
  const descuento = facturacion?.descuento;
  const tieneDescuento = descuento?.activo && descuento?.porcentaje;
  const precioUSD = tieneDescuento ? precioBaseUSD * (1 - descuento.porcentaje / 100) : precioBaseUSD;
  const precioARS = tipoCambio ? precioUSD * tipoCambio.valor : null;
  const vencPlan = facturacion?.fechaVencimientoPlan?.toDate ? facturacion.fechaVencimientoPlan.toDate() : facturacion?.fechaVencimiento?.toDate ? facturacion.fechaVencimiento.toDate() : null;
  const vencPago = facturacion?.fechaVencimientoPago?.toDate ? facturacion.fechaVencimientoPago.toDate() : null;
  const hoy = new Date();
  const diasParaCobro = vencPago ? Math.ceil((vencPago - hoy) / (1000 * 60 * 60 * 24)) : null;
  const alertaPago = diasParaCobro !== null && diasParaCobro <= 3 && diasParaCobro >= 0;
  const pagoVencido = diasParaCobro !== null && diasParaCobro < 0;

  if (userRole !== 'gestion_general') return (
    <div style={{ padding: '48px', textAlign: 'center', color: theme.secondary, fontFamily: "'Montserrat', sans-serif" }}>No tenés acceso a este módulo.</div>
  );

  return (
    <div style={{ minHeight: '100vh', background: theme.bg, fontFamily: "'Montserrat', sans-serif" }}>
      <Header titulo="Configuración" backLabel="Dashboard" backRuta="/dashboard" />
      <div style={{ padding: '24px', background: theme.bg, display: 'flex', flexDirection: 'column', gap: '20px' }}>

        <div style={{ background: theme.bgCard, borderRadius: '12px', border: `0.5px solid ${theme.border}`, padding: '20px' }}>
          <div style={{ fontSize: '11px', fontWeight: '700', color: theme.accent, letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: '16px' }}>Datos de la clínica</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '12px' }}>
            <div style={{ gridColumn: '1 / -1' }}><label style={labelStyle}>NOMBRE DE LA CLÍNICA</label><input value={form.nombre} onChange={e => setForm({...form, nombre: e.target.value})} style={inputStyle} /></div>
            <div><label style={labelStyle}>TELÉFONO</label><input value={form.telefono} onChange={e => setForm({...form, telefono: e.target.value})} style={inputStyle} /></div>
            <div><label style={labelStyle}>SITIO WEB</label><input value={form.web} onChange={e => setForm({...form, web: e.target.value})} placeholder="https://..." style={inputStyle} /></div>
            <div><label style={labelStyle}>DIRECCIÓN</label><input value={form.direccion} onChange={e => setForm({...form, direccion: e.target.value})} style={inputStyle} /></div>
            <div><label style={labelStyle}>CIUDAD</label><input value={form.ciudad} onChange={e => setForm({...form, ciudad: e.target.value})} style={inputStyle} /></div>
            <div><label style={labelStyle}>PROVINCIA</label><input value={form.provincia} onChange={e => setForm({...form, provincia: e.target.value})} style={inputStyle} /></div>
            <div style={{ gridColumn: '1 / -1' }}><label style={labelStyle}>DESCRIPCIÓN</label><textarea value={form.descripcion} onChange={e => setForm({...form, descripcion: e.target.value})} rows={2} style={{ ...inputStyle, resize: 'vertical' }} /></div>
          </div>
        </div>

        <div style={{ background: theme.bgCard, borderRadius: '12px', border: `0.5px solid ${theme.border}`, padding: '20px' }}>
          <div style={{ fontSize: '11px', fontWeight: '700', color: theme.accent, letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: '4px' }}>Nombres de médicos</div>
          <div style={{ fontSize: '12px', color: theme.secondary, marginBottom: '16px' }}>Estos nombres aparecerán en agenda, historia clínica y todos los módulos del sistema.</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '12px' }}>
            {[['medico1','MÉDICO 1'],['medico2','MÉDICO 2'],['medico3','MÉDICO 3'],['medico4','MÉDICO 4']].map(([key, label]) => (
              <div key={key}>
                <label style={labelStyle}>{label}</label>
                <input value={form.medicos[key]} onChange={e => setForm({...form, medicos: {...form.medicos, [key]: e.target.value}})} placeholder={`Nombre del ${label.toLowerCase()}...`} style={inputStyle} />
              </div>
            ))}
          </div>
        </div>

        {plan && facturacion && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {(alertaPago || pagoVencido || facturacion?.pagoPendienteAprobacion) && (
              <div style={{ background: pagoVencido ? theme.errorBg : facturacion?.pagoPendienteAprobacion ? theme.successBg : theme.warningBg, border: `0.5px solid ${pagoVencido ? theme.error : facturacion?.pagoPendienteAprobacion ? theme.success : theme.warning}`, borderRadius: '10px', padding: '14px 18px' }}>
                <div style={{ fontSize: '14px', fontWeight: '600', color: pagoVencido ? theme.error : facturacion?.pagoPendienteAprobacion ? theme.success : theme.warning }}>
                  {facturacion?.pagoPendienteAprobacion
                    ? '✓ Comprobante enviado — pendiente de aprobación'
                    : pagoVencido
                      ? '⚠ Tu pago está vencido. Por favor regularizá tu situación.'
                      : `⚠ Tu pago vence en ${diasParaCobro} día${diasParaCobro !== 1 ? 's' : ''}`
                  }
                </div>
              </div>
            )}

            <div style={{ background: theme.bgCard, borderRadius: '12px', border: `0.5px solid ${theme.border}`, padding: '20px' }}>
              <div style={{ fontSize: '11px', fontWeight: '700', color: theme.accent, letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: '16px' }}>Información del plan</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '10px', marginBottom: '16px' }}>
                {[
                  { label: 'PLAN ACTIVO', valor: (plan || '—').toUpperCase(), color: theme.accent },
                  { label: 'ESTADO', valor: config?.estado || '—', color: theme.success },
                  { label: 'INICIO DEL PLAN', valor: facturacion?.fechaInicio?.toDate ? facturacion.fechaInicio.toDate().toLocaleDateString('es-AR') : '—', color: theme.primary },
                  { label: 'VENCIMIENTO DEL PAGO', valor: vencPago ? vencPago.toLocaleDateString('es-AR') : '—', color: alertaPago || pagoVencido ? theme.error : theme.warning },
                  { label: 'VENCIMIENTO DEL PLAN', valor: vencPlan ? vencPlan.toLocaleDateString('es-AR') : '—', color: theme.primary },
                ].map(item => (
                  <div key={item.label} style={{ background: theme.bg, padding: '10px 12px', borderRadius: '8px', border: `0.5px solid ${theme.border}` }}>
                    <div style={{ fontSize: '10px', color: theme.secondary, marginBottom: '2px', fontWeight: '500', letterSpacing: '1px' }}>{item.label}</div>
                    <div style={{ fontSize: '13px', fontWeight: '600', color: item.color }}>{item.valor}</div>
                  </div>
                ))}
              </div>
              <div style={{ fontSize: '12px', color: theme.secondary, marginTop: '8px', fontStyle: 'italic' }}>
                🔄 Este plan se renueva automáticamente todos los meses.
                </div>

              <div style={{ background: theme.infoBg, borderRadius: '10px', padding: '16px', marginBottom: '16px' }}>
                <div style={{ fontSize: '12px', fontWeight: '700', color: theme.accent, marginBottom: '10px', letterSpacing: '1px' }}>VALOR A ABONAR</div>
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '8px' }}>
                  <div style={{ background: theme.bgCard, padding: '10px 16px', borderRadius: '8px', flex: 1, minWidth: '120px' }}>
                    <div style={{ fontSize: '10px', color: theme.secondary, marginBottom: '2px' }}>EN DÓLARES</div>
                    {tieneDescuento && <div style={{ fontSize: '12px', color: theme.secondary, textDecoration: 'line-through' }}>USD {precioBaseUSD}</div>}
                    <div style={{ fontSize: '22px', fontWeight: '700', color: theme.accent }}>USD {precioUSD.toFixed(0)}</div>
                    {tieneDescuento && <div style={{ fontSize: '11px', color: theme.success, fontWeight: '500' }}>−{descuento.porcentaje}% descuento</div>}
                  </div>
                  {precioARS && (
                    <div style={{ background: theme.bgCard, padding: '10px 16px', borderRadius: '8px', flex: 1, minWidth: '120px' }}>
                      <div style={{ fontSize: '10px', color: theme.secondary, marginBottom: '2px' }}>EN PESOS (TC BNA ${tipoCambio?.valor?.toLocaleString('es-AR')})</div>
                      <div style={{ fontSize: '22px', fontWeight: '700', color: theme.primary }}>${(precioARS).toLocaleString('es-AR', { maximumFractionDigits: 0 })}</div>
                    </div>
                  )}
                </div>
              </div>

              <div style={{ background: theme.bgCard, borderRadius: '10px', padding: '16px', marginBottom: '16px', border: `0.5px solid ${theme.border}` }}>
                <div style={{ fontSize: '12px', fontWeight: '700', color: theme.secondary, marginBottom: '12px', letterSpacing: '1px' }}>DATOS PARA TRANSFERENCIA</div>
                <div style={{ display: 'flex', gap: '6px', marginBottom: '12px' }}>
                  {['ARS', 'USD'].map(m => (
                    <button key={m} onClick={() => setMonedaPago(m)} style={{ padding: '5px 14px', borderRadius: '8px', cursor: 'pointer', border: monedaPago === m ? 'none' : `0.5px solid ${theme.borderInput}`, background: monedaPago === m ? theme.accent : 'transparent', color: monedaPago === m ? '#FFFFFF' : theme.secondary, fontFamily: "'Montserrat', sans-serif", fontWeight: '600', fontSize: '12px' }}>
                      {m === 'ARS' ? 'Transferir en pesos' : 'Transferir en dólares'}
                    </button>
                  ))}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '8px' }}>
                  {(monedaPago === 'ARS' ? [
                    ['TITULAR', DATOS_BANCARIOS_ARS.titular],
                    ['CUENTA', DATOS_BANCARIOS_ARS.cuenta],
                    ['ALIAS', DATOS_BANCARIOS_ARS.alias],
                    ['CBU', DATOS_BANCARIOS_ARS.cbu],
                    ['CUIL', DATOS_BANCARIOS_ARS.cuil],
                  ] : [
                    ['TITULAR', DATOS_BANCARIOS_USD.titular],
                    ['CUENTA', DATOS_BANCARIOS_USD.cuenta],
                    ['ALIAS', DATOS_BANCARIOS_USD.alias],
                    ['CBU', DATOS_BANCARIOS_USD.cbu],
                    ['CUIL', DATOS_BANCARIOS_USD.cuil],
                  ]).map(([label, valor]) => (
                    <div key={label} style={{ background: theme.bg, padding: '8px 12px', borderRadius: '6px' }}>
                      <div style={{ fontSize: '10px', color: theme.secondary, marginBottom: '2px', fontWeight: '500' }}>{label}</div>
                      <div style={{ fontSize: '13px', color: theme.primary, fontWeight: '600', fontFamily: 'monospace', letterSpacing: '0.5px' }}
                        onClick={() => { navigator.clipboard.writeText(valor); }}
                        style={{ cursor: 'pointer', fontSize: '13px', color: theme.primary, fontWeight: '600' }}
                      >{valor}</div>
                    </div>
                  ))}
                </div>
                <div style={{ fontSize: '11px', color: theme.secondary, marginTop: '8px' }}>Hacé clic en cualquier dato para copiarlo.</div>
              </div>

              {!facturacion?.pagoPendienteAprobacion && (
                <div style={{ background: theme.bgCard, borderRadius: '10px', padding: '16px', border: `0.5px solid ${theme.border}` }}>
                  <div style={{ fontSize: '12px', fontWeight: '700', color: theme.secondary, marginBottom: '12px', letterSpacing: '1px' }}>ENVIAR COMPROBANTE DE PAGO</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <div>
                      <label style={labelStyle}>MONEDA EN QUE PAGASTE</label>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        {['USD', 'ARS'].map(m => (
                          <button key={m} onClick={() => setMonedaPago(m)} style={{ padding: '6px 16px', borderRadius: '8px', cursor: 'pointer', border: monedaPago === m ? 'none' : `0.5px solid ${theme.borderInput}`, background: monedaPago === m ? theme.accent : 'transparent', color: monedaPago === m ? '#FFFFFF' : theme.secondary, fontFamily: "'Montserrat', sans-serif", fontWeight: '600', fontSize: '13px' }}>
                            {m}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div><label style={labelStyle}>NÚMERO DE REFERENCIA / COMPROBANTE</label><input value={referencia} onChange={e => setReferencia(e.target.value)} placeholder="N° de transferencia, CBU origen..." style={inputStyle} /></div>
                    <div>
                      <label style={labelStyle}>ADJUNTAR COMPROBANTE (opcional)</label>
                      <input type="file" accept=".jpg,.jpeg,.png,.pdf" onChange={e => setComprobante(e.target.files[0] || null)} style={{ fontSize: '13px', color: theme.secondary, fontFamily: "'Montserrat', sans-serif" }} />
                      {comprobante && <span style={{ fontSize: '11px', marginTop: '4px', display: 'inline-block', color: theme.success, background: theme.successBg, padding: '3px 8px', borderRadius: '99px' }}>{comprobante.name}</span>}
                    </div>
                    <button onClick={enviarComprobante} disabled={enviandoPago} style={{ ...btnPrimaryStyle, alignSelf: 'flex-start' }}>
                      {enviandoPago ? 'Enviando...' : 'Enviar comprobante'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
{historialPagos.length > 0 && (
  <div style={{ background: theme.bgCard, borderRadius: '10px', border: `0.5px solid ${theme.border}`, padding: '16px' }}>
    <div style={{ fontSize: '12px', fontWeight: '700', color: theme.secondary, marginBottom: '12px', letterSpacing: '1px' }}>HISTORIAL DE PAGOS</div>
    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
      {historialPagos.map((p, i) => {
        const fecha = p.fecha?.toDate ? p.fecha.toDate().toLocaleDateString('es-AR') : '—';
        return (
          <div key={i} style={{ background: theme.bg, borderRadius: '8px', padding: '10px 14px', border: `0.5px solid ${theme.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: '13px', fontWeight: '500', color: theme.primary }}>Transferencia · {fecha}</div>
              {p.referencia && <div style={{ fontSize: '11px', color: theme.secondary }}>Ref: {p.referencia}</div>}
            </div>
            <span style={{ fontSize: '15px', fontWeight: '700', color: theme.success }}>
              {p.moneda === 'USD' ? 'USD ' : '$'}{parseFloat(p.monto || 0).toLocaleString('es-AR')}
            </span>
          </div>
        );
      })}
    </div>
  </div>
)}
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <button onClick={guardar} disabled={guardando} style={{ ...btnPrimaryStyle, padding: '10px 24px' }}>
            {guardando ? 'Guardando...' : 'Guardar configuración'}
          </button>
          {guardado && <span style={{ fontSize: '13px', color: theme.success, fontWeight: '500' }}>✓ Configuración guardada correctamente</span>}
        </div>
      </div>
    </div>
  );
}